import React, {useState} from "react"
import {
	Box,
	Button,
	Grid,
	TextField,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	FormControlLabel,
	Checkbox,
	CircularProgress,
} from "@mui/material"
import SearchIcon from "@mui/icons-material/Search"

import {AdvancedSearchParams} from "@indebted/api"

type ClientOption = {
	ID: string
	Name: string
}

type AdvancedSearchPanelProps = {
	clients: ClientOption[]
	onSubmit: (params: AdvancedSearchParams) => void
	onClear?: () => void
	loading?: boolean
	hidden?: boolean
}

type FormValues = {
	firstName: string
	middleName: string
	lastName: string
	phone: string
	email: string
	ssnLast4: string
	dob: string
	addressLine1: string
	postcode: string
	clientID: string
	includeHistoricContactDetails: boolean
}

const emptyForm: FormValues = {
	firstName: "",
	middleName: "",
	lastName: "",
	phone: "",
	email: "",
	ssnLast4: "",
	dob: "",
	addressLine1: "",
	postcode: "",
	clientID: "",
	includeHistoricContactDetails: false,
}

// Keep every control the same height regardless of type (text, date, select).
const fieldSx = {"& .MuiInputBase-root": {minHeight: 40}}

// Light client-side email check; the backend re-validates and normalizes.
const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)

// canSubmitAdvancedSearch mirrors the backend's narrowing rules so the agent gets
// immediate feedback: at least one of {ssnLast4, lastName, phone, email} must be
// provided, and a bare ssnLast4 (high-collision) additionally needs dob or lastName.
// Exported for unit testing.
function canSubmitAdvancedSearch(values: FormValues): boolean {
	const hasNarrowingField = [values.ssnLast4, values.lastName, values.phone, values.email].some(
		(value) => value.trim() !== "",
	)
	if (!hasNarrowingField) return false

	if (values.ssnLast4.trim() !== "" && values.dob.trim() === "" && values.lastName.trim() === "") {
		return false
	}

	return true
}

function toParams(values: FormValues): AdvancedSearchParams {
	return {
		clientID: values.clientID,
		ssnLast4: values.ssnLast4,
		firstName: values.firstName,
		middleName: values.middleName,
		lastName: values.lastName,
		phone: values.phone,
		email: values.email,
		addressLine1: values.addressLine1,
		postcode: values.postcode,
		dob: values.dob,
		includeHistoricContactDetails: values.includeHistoricContactDetails,
	}
}

function AdvancedSearchPanel({clients, onSubmit, onClear, loading = false, hidden = false}: AdvancedSearchPanelProps) {
	const [values, setValues] = useState<FormValues>(emptyForm)

	const setField = <K extends keyof FormValues>(field: K, value: FormValues[K]) =>
		setValues((prev) => ({...prev, [field]: value}))

	// Dirty when any field differs from the empty defaults; gates the Clear button.
	const isDirty = (Object.keys(emptyForm) as (keyof FormValues)[]).some((key) => values[key] !== emptyForm[key])

	const handleClear = () => {
		setValues(emptyForm)
		onClear?.()
	}

	const text = (field: keyof FormValues, label: string, extra?: object, sanitize?: (value: string) => string) => (
		<TextField
			label={label}
			variant="outlined"
			fullWidth
			size="small"
			sx={fieldSx}
			value={values[field] as string}
			onChange={(e) => setField(field, sanitize ? sanitize(e.target.value) : e.target.value)}
			{...extra}
		/>
	)

	const emailValid = values.email.trim() === "" || isValidEmail(values.email.trim())
	const canSubmit = canSubmitAdvancedSearch(values) && emailValid && !loading

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault()
		if (!canSubmit) return
		onSubmit(toParams(values))
	}

	const fieldRow = (children: React.ReactNode) => (
		<Grid size={12}>
			<Grid container spacing={2}>
				{children}
			</Grid>
		</Grid>
	)

	return (
		<Box
			component="form"
			onSubmit={handleSubmit}
			hidden={hidden}
			aria-label="Advanced search"
			sx={{mx: 3, p: 3, border: 1, borderColor: "divider", borderRadius: 2}}
		>
			<Grid container spacing={3}>
				{fieldRow(
					<>
						<Grid size={{xs: 12, sm: 4}}>{text("firstName", "First name")}</Grid>
						<Grid size={{xs: 12, sm: 4}}>{text("middleName", "Middle name")}</Grid>
						<Grid size={{xs: 12, sm: 4}}>{text("lastName", "Last name")}</Grid>
					</>,
				)}

				{fieldRow(
					<>
						<Grid size={{xs: 12, sm: 6}}>{text("phone", "Phone")}</Grid>
						<Grid size={{xs: 12, sm: 6}}>
							<TextField
								label="Email"
								type="email"
								variant="outlined"
								fullWidth
								size="small"
								sx={fieldSx}
								value={values.email}
								onChange={(e) => setField("email", e.target.value)}
								error={!emailValid}
								helperText={emailValid ? undefined : "Enter a valid email address"}
							/>
						</Grid>
					</>,
				)}

				{fieldRow(
					<>
						<Grid size={{xs: 12, sm: 6}}>
							{text(
								"ssnLast4",
								"SSN (last 4)",
								{slotProps: {htmlInput: {maxLength: 4, inputMode: "numeric"}}},
								(value) => value.replace(/\D/g, ""),
							)}
						</Grid>
						<Grid size={{xs: 12, sm: 6}}>
							{text("dob", "Date of birth", {type: "date", slotProps: {inputLabel: {shrink: true}}})}
						</Grid>
					</>,
				)}

				{fieldRow(
					<>
						<Grid size={{xs: 12, sm: 8}}>{text("addressLine1", "Address line 1")}</Grid>
						<Grid size={{xs: 12, sm: 4}}>{text("postcode", "Postcode")}</Grid>
					</>,
				)}

				{fieldRow(
					<Grid size={12}>
						<FormControl fullWidth size="small" sx={fieldSx}>
							<InputLabel id="advanced-search-client-label">Client</InputLabel>
							<Select
								labelId="advanced-search-client-label"
								label="Client"
								value={values.clientID}
								onChange={(e) => setField("clientID", e.target.value)}
								MenuProps={{PaperProps: {sx: {maxHeight: 320}}}}
							>
								<MenuItem value="">
									<em>All clients</em>
								</MenuItem>
								{clients.map((client) => (
									<MenuItem key={client.ID} value={client.ID}>
										{client.Name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>,
				)}

				<Grid size={12}>
					<FormControlLabel
						control={
							<Checkbox
								checked={values.includeHistoricContactDetails}
								onChange={(e) => setField("includeHistoricContactDetails", e.target.checked)}
							/>
						}
						label="Include historic contact details"
					/>
				</Grid>

				<Grid size={12}>
					<Box display="flex" justifyContent="flex-end" gap={2}>
						<Button
							type="button"
							variant="outlined"
							size="large"
							onClick={handleClear}
							disabled={!isDirty || loading}
							sx={{textTransform: "none", fontWeight: 600, minWidth: 120, borderRadius: 2}}
						>
							Clear
						</Button>
						<Button
							type="submit"
							variant="contained"
							size="large"
							disabled={!canSubmit}
							startIcon={loading ? null : <SearchIcon />}
							sx={{textTransform: "none", fontWeight: 600, minWidth: 160, borderRadius: 2}}
						>
							{loading ? <CircularProgress size={24} color="inherit" /> : "Search"}
						</Button>
					</Box>
				</Grid>
			</Grid>
		</Box>
	)
}

export {AdvancedSearchPanel, canSubmitAdvancedSearch}
export type {AdvancedSearchPanelProps, ClientOption, FormValues}
